/**
 * Conversation — a message log between participants
 *
 * FVC 170 — brain step 2. New entity kind, additive.
 *
 * `participants` is a free-form list of participant identifiers (operator,
 * agent ids, system ids — no shape constraint at v1).
 * `messages` is ordered chronologically; each message has its own id and
 * an optional `meta` blob for structured payloads (tool calls, refs, etc.).
 */

export interface ConversationMessage {
  id: string;
  author: string;
  text: string;
  ts: string;
  meta?: Record<string, unknown>;
}

export interface Conversation {
  id: string;
  fcid: string;
  project: string;
  title: string;
  participants: string[];
  /**
   * F 282 — the other project in a cross-project conversation, or absent.
   *
   * NULLABLE and absent-by-default: a conversation with no cross-project
   * reference has no `relatedProject`, and that is the ordinary case. This is
   * the same nullable-field-means-the-exception move Project.lane made in F 275.
   *
   * It is a STRING project id, not a nested object, for the reason Project.lane
   * gives: `id` is a string, fcid segments are `[a-z][a-z0-9-]*`, and HQ's
   * `thread_id` is an integer column this substrate must be able to name.
   *
   * A cross-project conversation exists because BOTH projects declared the link
   * (F 273 decision two). Reading it across the boundary still requires the
   * READER to hold 'fleet-read' — the link is not a capability grant.
   */
  relatedProject?: string;
  messages: ConversationMessage[];
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
}

export function createConversationMessage(input: {
  author: string;
  text: string;
  ts?: string;
  meta?: Record<string, unknown>;
  id?: string;
}): ConversationMessage {
  return {
    id: input.id ?? generateMessageId(),
    author: input.author,
    text: input.text,
    ts: input.ts ?? new Date().toISOString(),
    meta: input.meta,
  };
}

export function createConversation(input: {
  title: string;
  project: string;
  participants?: string[];
  messages?: ConversationMessage[];
  /** F 282 — pass it and the factory carries it; omit it and it stays absent. */
  relatedProject?: string;
  id?: string;
  createdAt?: string;
}): Conversation {
  const now = new Date().toISOString();
  const id = input.id ?? generateConversationId();
  return {
    id,
    fcid: `fcid://conversation/${id}`,
    title: input.title,
    project: input.project,
    participants: input.participants ?? [],
    // Carried ONLY when supplied, so "no cross-project link" stays absent
    // rather than becoming an empty string.
    ...(input.relatedProject !== undefined ? { relatedProject: input.relatedProject } : {}),
    messages: input.messages ?? [],
    createdAt: input.createdAt ?? now,
    updatedAt: now,
    closedAt: null,
  };
}

export function generateMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function generateConversationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `conv-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function isConversationMessage(value: unknown): value is ConversationMessage {
  if (!value || typeof value !== 'object') return false;
  const m = value as Record<string, unknown>;
  return (
    typeof m.id === 'string' &&
    typeof m.author === 'string' &&
    typeof m.text === 'string' &&
    typeof m.ts === 'string'
  );
}

export function isConversation(value: unknown): value is Conversation {
  if (!value || typeof value !== 'object') return false;
  const c = value as Record<string, unknown>;
  return (
    typeof c.id === 'string' &&
    typeof c.title === 'string' &&
    typeof c.project === 'string' &&
    typeof c.fcid === 'string' &&
    c.fcid.startsWith('fcid://conversation/') &&
    Array.isArray(c.messages) &&
    Array.isArray(c.participants)
  );
}

export function appendMessage(conv: Conversation, msg: ConversationMessage): Conversation {
  const updated: Conversation = {
    ...conv,
    messages: [...conv.messages, msg],
    updatedAt: new Date().toISOString(),
  };
  return updated;
}
